import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";

export type Category = { title: string; id: number | null };

/** Display title for a tag id we haven't resolved a real name for yet. */
export const placeholderTagTitle = (id: number) => `Tag #${id}`;

/** Whether a category is still showing a placeholder rather than a real name. */
export const isPlaceholderTag = (cat: Category) =>
  cat.id != null && cat.title === placeholderTagTitle(cat.id);

/**
 * Tag ids -> Category objects for display.
 *
 * `menuCategories` only carries the 51 curated tags the headline menu shows,
 * but the concierge emits ids from the whole tag system and a saved filter can
 * hold any of them. This used to drop a miss on the floor — no chip, no
 * warning, the tag simply gone from a filter the user had just been shown — so
 * an unknown id is now kept under a placeholder title that FilterBuilderBrowser
 * refines once `/tags/ids` returns the real name. Same shape TagWatchlistModal
 * already uses for the identical problem.
 */
export const toCategories = (tagIds: (string | number)[]): Category[] =>
  tagIds
    .map((raw): Category | null => {
      // Not `Number(raw)` alone: `Number("")` is 0, and the save payload writes
      // "" for a null id, so a round-trip would invent a "Tag #0".
      const trimmed = String(raw).trim();
      const id = Number(trimmed);
      if (!trimmed || !Number.isFinite(id)) return null;
      const item = menuCategories
        .flatMap((c) => c.items)
        .find((i) => i.id === id);
      return item
        ? { title: item.title, id: item.id }
        : { title: placeholderTagTitle(id), id };
    })
    .filter((c): c is Category => c !== null);
